import { type Static, Type } from "@sinclair/typebox";
import { Value } from "@sinclair/typebox/value";
import {
  CustomerReservationNumberSchema,
  type CustomerReservationState,
  CustomerReservationStateSchema,
  deriveCustomerReservationNumber,
} from "./customer-reservation";

const StableIdSchema = Type.String({
  minLength: 1,
  maxLength: 128,
  pattern: "^[A-Za-z0-9][A-Za-z0-9._:-]{0,127}$",
});
const IdempotencyKeySchema = Type.String({
  pattern: "^[A-Za-z0-9][A-Za-z0-9._:-]{7,127}$",
});
const SecretSchema = Type.String({ minLength: 43, maxLength: 43, pattern: "^[A-Za-z0-9_-]{43}$" });
const CanonicalDateTimeSchema = Type.String({ minLength: 24, maxLength: 24 });
const NullableDateTimeSchema = Type.Union([CanonicalDateTimeSchema, Type.Null()]);
const CustomerNameSchema = Type.String({ minLength: 1, maxLength: 60 });
const CustomerMobileSchema = Type.String({ pattern: "^1[3-9][0-9]{9}$" });
const CustomerMobileMaskedSchema = Type.String({ pattern: "^1[3-9][0-9]\\*{4}[0-9]{4}$" });
const GroupBuyProviderSchema = Type.Union([Type.Literal("MEITUAN"), Type.Literal("DOUYIN")]);
const FNV_1A_64_OFFSET = 0xcbf29ce484222325n;
const FNV_1A_64_PRIME = 0x100000001b3n;

export const CustomerSelfServiceBootstrapReadModelSchema = Type.Object(
  {
    contractVersion: Type.Literal("1.0.0"),
    tenantId: StableIdSchema,
    storeId: StableIdSchema,
    storeName: Type.String({ minLength: 1, maxLength: 80 }),
    customerEntryUrl: Type.String({ minLength: 1, maxLength: 2_048 }),
    reservationEnabled: Type.Boolean(),
    groupBuyRequestEnabled: Type.Boolean(),
  },
  { additionalProperties: false },
);
export type CustomerSelfServiceBootstrapReadModel = Static<
  typeof CustomerSelfServiceBootstrapReadModelSchema
>;

export const CreateCustomerVisitSessionRequestSchema = Type.Object(
  {
    contractVersion: Type.Literal("1.0.0"),
    tenantId: StableIdSchema,
    storeId: StableIdSchema,
    customerName: CustomerNameSchema,
    customerMobile: CustomerMobileSchema,
    setupSecret: SecretSchema,
    csrfToken: SecretSchema,
    idempotencyKey: IdempotencyKeySchema,
  },
  { additionalProperties: false },
);
export type CreateCustomerVisitSessionRequest = Static<
  typeof CreateCustomerVisitSessionRequestSchema
>;

export const CustomerVisitSessionReadModelSchema = Type.Object(
  {
    contractVersion: Type.Literal("1.0.0"),
    visitId: StableIdSchema,
    tenantId: StableIdSchema,
    storeId: StableIdSchema,
    customerName: CustomerNameSchema,
    customerMobileMasked: CustomerMobileMaskedSchema,
    status: Type.Union([Type.Literal("ACTIVE"), Type.Literal("REVOKED")]),
    createdAt: CanonicalDateTimeSchema,
    expiresAt: CanonicalDateTimeSchema,
    revokedAt: NullableDateTimeSchema,
  },
  { additionalProperties: false },
);
export type CustomerVisitSessionReadModel = Static<typeof CustomerVisitSessionReadModelSchema>;

export const SelfServiceCustomerReservationCreateRequestSchema = Type.Object(
  {
    contractVersion: Type.Literal("1.0.0"),
    partySize: Type.Integer({ minimum: 1, maximum: 20 }),
    requestedStartAt: CanonicalDateTimeSchema,
    durationMinutes: Type.Integer({ minimum: 30, maximum: 720, multipleOf: 30 }),
    note: Type.Union([Type.String({ minLength: 1, maxLength: 300 }), Type.Null()]),
    depositFen: Type.Literal(0),
    discountFen: Type.Literal(0),
    idempotencyKey: IdempotencyKeySchema,
  },
  { additionalProperties: false },
);
export type SelfServiceCustomerReservationCreateRequest = Static<
  typeof SelfServiceCustomerReservationCreateRequestSchema
>;

export const SelfServiceCustomerReservationCancelRequestSchema = Type.Object(
  {
    contractVersion: Type.Literal("1.0.0"),
    expectedReservationVersion: Type.Integer({ minimum: 1, maximum: Number.MAX_SAFE_INTEGER }),
    reason: Type.String({ minLength: 2, maxLength: 200 }),
    idempotencyKey: IdempotencyKeySchema,
  },
  { additionalProperties: false },
);
export type SelfServiceCustomerReservationCancelRequest = Static<
  typeof SelfServiceCustomerReservationCancelRequestSchema
>;

export const CustomerSelfServiceReservationReadModelSchema = Type.Object(
  {
    contractVersion: Type.Literal("1.0.0"),
    reservationId: StableIdSchema,
    reservationNumber: CustomerReservationNumberSchema,
    reservationVersion: Type.Integer({ minimum: 1, maximum: Number.MAX_SAFE_INTEGER }),
    businessDate: Type.String({ pattern: "^[0-9]{4}-[0-9]{2}-[0-9]{2}$" }),
    partySize: Type.Integer({ minimum: 1, maximum: 20 }),
    requestedStartAt: CanonicalDateTimeSchema,
    requestedEndAt: CanonicalDateTimeSchema,
    note: Type.Union([Type.String({ minLength: 1, maxLength: 300 }), Type.Null()]),
    state: CustomerReservationStateSchema,
    queuePosition: Type.Union([
      Type.Integer({ minimum: 1, maximum: Number.MAX_SAFE_INTEGER }),
      Type.Null(),
    ]),
    estimatedWaitMinutes: Type.Union([
      Type.Integer({ minimum: 0, maximum: Number.MAX_SAFE_INTEGER }),
      Type.Null(),
    ]),
    waitEstimateKind: Type.Union([
      Type.Literal("NOT_IN_QUEUE"),
      Type.Literal("READY_NOW"),
      Type.Literal("CURRENT_IDLE_TABLES"),
      Type.Literal("UNAVAILABLE_NO_IDLE_TABLE"),
    ]),
    cancellationAllowed: Type.Boolean(),
    assignedTableNumber: Type.Union([Type.String({ minLength: 1, maxLength: 32 }), Type.Null()]),
    orderNumber: Type.Union([Type.String({ minLength: 1, maxLength: 128 }), Type.Null()]),
    createdAt: CanonicalDateTimeSchema,
    updatedAt: CanonicalDateTimeSchema,
  },
  { additionalProperties: false },
);
export type CustomerSelfServiceReservationReadModel = Static<
  typeof CustomerSelfServiceReservationReadModelSchema
>;

export const SelfServiceGroupBuyRequestCreateRequestSchema = Type.Object(
  {
    contractVersion: Type.Literal("1.0.0"),
    provider: GroupBuyProviderSchema,
    voucherCodeSuffix: Type.String({ minLength: 2, maxLength: 4, pattern: "^[A-Za-z0-9]+$" }),
    note: Type.Union([Type.String({ minLength: 2, maxLength: 200 }), Type.Null()]),
    idempotencyKey: IdempotencyKeySchema,
  },
  { additionalProperties: false },
);
export type SelfServiceGroupBuyRequestCreateRequest = Static<
  typeof SelfServiceGroupBuyRequestCreateRequestSchema
>;

export const CustomerGroupBuyRequestStatusSchema = Type.Union([
  Type.Literal("PENDING"),
  Type.Literal("PROCESSING"),
  Type.Literal("COMPLETED"),
  Type.Literal("REJECTED"),
  Type.Literal("NEEDS_REVIEW"),
]);
export type CustomerGroupBuyRequestStatus = Static<typeof CustomerGroupBuyRequestStatusSchema>;

export const CustomerGroupBuyRequestReadModelSchema = Type.Object(
  {
    contractVersion: Type.Literal("1.0.0"),
    requestId: StableIdSchema,
    requestNumber: Type.String({ pattern: "^TG-[0-9]{8}-[0-9A-F]{12}$" }),
    requestVersion: Type.Integer({ minimum: 1, maximum: Number.MAX_SAFE_INTEGER }),
    businessDate: Type.String({ pattern: "^[0-9]{4}-[0-9]{2}-[0-9]{2}$" }),
    customerName: CustomerNameSchema,
    customerMobileMasked: CustomerMobileMaskedSchema,
    provider: GroupBuyProviderSchema,
    voucherCodeSuffix: Type.String({ minLength: 2, maxLength: 4, pattern: "^[A-Z0-9]+$" }),
    note: Type.Union([Type.String({ minLength: 2, maxLength: 200 }), Type.Null()]),
    status: CustomerGroupBuyRequestStatusSchema,
    linkedRedemptionNumber: Type.Union([
      Type.String({ pattern: "^HX-[0-9]{8}-[0-9A-F]{12}$" }),
      Type.Null(),
    ]),
    createdAt: CanonicalDateTimeSchema,
    updatedAt: CanonicalDateTimeSchema,
  },
  { additionalProperties: false },
);
export type CustomerGroupBuyRequestReadModel = Static<
  typeof CustomerGroupBuyRequestReadModelSchema
>;

export const CustomerGroupBuyRequestMutationResultSchema = Type.Object(
  {
    operation: Type.Literal("CREATE"),
    request: CustomerGroupBuyRequestReadModelSchema,
  },
  { additionalProperties: false },
);
export type CustomerGroupBuyRequestMutationResult = Static<
  typeof CustomerGroupBuyRequestMutationResultSchema
>;

export const CustomerSelfServiceWorkspaceSchema = Type.Object(
  {
    session: CustomerVisitSessionReadModelSchema,
    storeName: Type.String({ minLength: 1, maxLength: 80 }),
    timeZone: Type.String({ minLength: 1, maxLength: 80 }),
    currentBusinessDate: Type.String({ pattern: "^[0-9]{4}-[0-9]{2}-[0-9]{2}$" }),
    reservationEnabled: Type.Boolean(),
    groupBuyRequestEnabled: Type.Boolean(),
    reservations: Type.Array(CustomerSelfServiceReservationReadModelSchema, { maxItems: 100 }),
    groupBuyRequests: Type.Array(CustomerGroupBuyRequestReadModelSchema, { maxItems: 100 }),
    generatedAt: CanonicalDateTimeSchema,
  },
  { additionalProperties: false },
);
export type CustomerSelfServiceWorkspace = Static<typeof CustomerSelfServiceWorkspaceSchema>;

export const CustomerSelfServiceMutationResultSchema = Type.Object(
  {
    operation: Type.Union([Type.Literal("CREATE"), Type.Literal("CANCEL")]),
    reservation: CustomerSelfServiceReservationReadModelSchema,
  },
  { additionalProperties: false },
);
export type CustomerSelfServiceMutationResult = Static<
  typeof CustomerSelfServiceMutationResultSchema
>;

export const CustomerVisitSessionRevokeRequestSchema = Type.Object(
  {
    contractVersion: Type.Literal("1.0.0"),
    idempotencyKey: IdempotencyKeySchema,
  },
  { additionalProperties: false },
);
export type CustomerVisitSessionRevokeRequest = Static<
  typeof CustomerVisitSessionRevokeRequestSchema
>;

export const CustomerVisitSessionRevokeResultSchema = Type.Object(
  {
    session: CustomerVisitSessionReadModelSchema,
  },
  { additionalProperties: false },
);
export type CustomerVisitSessionRevokeResult = Static<
  typeof CustomerVisitSessionRevokeResultSchema
>;

function canonicalEpoch(value: string): number | null {
  const epoch = Date.parse(value);
  return Number.isFinite(epoch) && new Date(epoch).toISOString() === value ? epoch : null;
}

function isRealBusinessDate(value: string): boolean {
  if (!/^\d{4}-\d{2}-\d{2}$/u.test(value)) return false;
  const epoch = Date.parse(`${value}T00:00:00.000Z`);
  return Number.isFinite(epoch) && new Date(epoch).toISOString() === `${value}T00:00:00.000Z`;
}

export function deriveCustomerGroupBuyRequestNumber(
  businessDate: string,
  requestId: string,
): string {
  if (
    !isRealBusinessDate(businessDate) ||
    !/^[A-Za-z0-9][A-Za-z0-9._:-]{0,127}$/u.test(requestId)
  ) {
    throw new TypeError("invalid customer group-buy request identity");
  }
  let hash = FNV_1A_64_OFFSET;
  for (const character of `${businessDate}\0${requestId}`) {
    hash ^= BigInt(character.codePointAt(0) ?? 0);
    hash = BigInt.asUintN(64, hash * FNV_1A_64_PRIME);
  }
  const suffix = (hash & 0xffffffffffffn).toString(16).toUpperCase().padStart(12, "0");
  return `TG-${businessDate.replaceAll("-", "")}-${suffix}`;
}

export function isCustomerSelfServiceBootstrapReadModel(
  value: unknown,
): value is CustomerSelfServiceBootstrapReadModel {
  if (!Value.Check(CustomerSelfServiceBootstrapReadModelSchema, value)) return false;
  try {
    const url = new URL(value.customerEntryUrl);
    return (
      (url.protocol === "https:" || url.protocol === "http:") &&
      url.hostname.length > 0 &&
      url.username === "" &&
      url.password === "" &&
      url.pathname === "/customer" &&
      url.search === "" &&
      url.hash === "" &&
      (value.reservationEnabled || value.groupBuyRequestEnabled)
    );
  } catch {
    return false;
  }
}

export function isCreateCustomerVisitSessionRequest(
  value: unknown,
): value is CreateCustomerVisitSessionRequest {
  return (
    Value.Check(CreateCustomerVisitSessionRequestSchema, value) &&
    value.customerName === value.customerName.trim()
  );
}

export function isSelfServiceCustomerReservationCreateRequest(
  value: unknown,
): value is SelfServiceCustomerReservationCreateRequest {
  return (
    Value.Check(SelfServiceCustomerReservationCreateRequestSchema, value) &&
    canonicalEpoch(value.requestedStartAt) !== null &&
    value.note === (value.note === null ? null : value.note.trim())
  );
}

export function isSelfServiceCustomerReservationCancelRequest(
  value: unknown,
): value is SelfServiceCustomerReservationCancelRequest {
  return (
    Value.Check(SelfServiceCustomerReservationCancelRequestSchema, value) &&
    value.reason === value.reason.trim()
  );
}

export function isSelfServiceGroupBuyRequestCreateRequest(
  value: unknown,
): value is SelfServiceGroupBuyRequestCreateRequest {
  return (
    Value.Check(SelfServiceGroupBuyRequestCreateRequestSchema, value) &&
    value.voucherCodeSuffix === value.voucherCodeSuffix.trim().toUpperCase() &&
    value.note === (value.note === null ? null : value.note.trim())
  );
}

export function isCustomerVisitSessionReadModel(
  value: unknown,
): value is CustomerVisitSessionReadModel {
  if (!Value.Check(CustomerVisitSessionReadModelSchema, value)) return false;
  const createdAt = canonicalEpoch(value.createdAt);
  const expiresAt = canonicalEpoch(value.expiresAt);
  const revokedAt = value.revokedAt === null ? null : canonicalEpoch(value.revokedAt);
  return (
    createdAt !== null &&
    expiresAt !== null &&
    expiresAt > createdAt &&
    (value.status === "ACTIVE"
      ? value.revokedAt === null
      : revokedAt !== null && revokedAt >= createdAt && revokedAt <= expiresAt)
  );
}

const cancellableStates = new Set<CustomerReservationState>([
  "PENDING_CONFIRMATION",
  "CONFIRMED",
  "WAITING",
  "CALLED",
]);

export function isCustomerSelfServiceReservationReadModel(
  value: unknown,
): value is CustomerSelfServiceReservationReadModel {
  if (!Value.Check(CustomerSelfServiceReservationReadModelSchema, value)) return false;
  const startAt = canonicalEpoch(value.requestedStartAt);
  const endAt = canonicalEpoch(value.requestedEndAt);
  const createdAt = canonicalEpoch(value.createdAt);
  const updatedAt = canonicalEpoch(value.updatedAt);
  const durationMs = startAt === null || endAt === null ? 0 : endAt - startAt;
  let expectedReservationNumber: string;
  try {
    expectedReservationNumber = deriveCustomerReservationNumber(
      value.businessDate,
      value.reservationId,
    );
  } catch {
    return false;
  }
  if (
    !isRealBusinessDate(value.businessDate) ||
    value.reservationNumber !== expectedReservationNumber ||
    startAt === null ||
    endAt === null ||
    durationMs < 30 * 60_000 ||
    durationMs > 720 * 60_000 ||
    durationMs % (30 * 60_000) !== 0 ||
    createdAt === null ||
    updatedAt === null ||
    updatedAt < createdAt ||
    value.cancellationAllowed !== cancellableStates.has(value.state)
  ) {
    return false;
  }
  const queued = value.state === "WAITING" || value.state === "CALLED";
  if (queued) {
    if (value.queuePosition === null || value.waitEstimateKind === "NOT_IN_QUEUE") return false;
    if (
      value.waitEstimateKind === "UNAVAILABLE_NO_IDLE_TABLE"
        ? value.estimatedWaitMinutes !== null
        : value.estimatedWaitMinutes === null
    ) {
      return false;
    }
  } else if (
    value.queuePosition !== null ||
    value.estimatedWaitMinutes !== null ||
    value.waitEstimateKind !== "NOT_IN_QUEUE"
  ) {
    return false;
  }
  return value.state === "SEATED"
    ? value.assignedTableNumber !== null && value.orderNumber !== null
    : value.assignedTableNumber === null && value.orderNumber === null;
}

export function isCustomerSelfServiceWorkspace(
  value: unknown,
): value is CustomerSelfServiceWorkspace {
  if (!Value.Check(CustomerSelfServiceWorkspaceSchema, value)) return false;
  if (
    !isCustomerVisitSessionReadModel(value.session) ||
    value.session.status !== "ACTIVE" ||
    canonicalEpoch(value.generatedAt) === null ||
    !isRealBusinessDate(value.currentBusinessDate) ||
    (!value.reservationEnabled && !value.groupBuyRequestEnabled) ||
    !value.reservations.every(isCustomerSelfServiceReservationReadModel) ||
    !value.groupBuyRequests.every(isCustomerGroupBuyRequestReadModel) ||
    (!value.reservationEnabled && value.reservations.length > 0) ||
    (!value.groupBuyRequestEnabled && value.groupBuyRequests.length > 0)
  ) {
    return false;
  }
  try {
    new Intl.DateTimeFormat("zh-CN", { timeZone: value.timeZone }).format(new Date(0));
  } catch {
    return false;
  }
  return (
    new Set(value.reservations.map((item) => item.reservationId)).size ===
      value.reservations.length &&
    new Set(value.reservations.map((item) => item.reservationNumber)).size ===
      value.reservations.length &&
    new Set(value.groupBuyRequests.map((item) => item.requestId)).size ===
      value.groupBuyRequests.length &&
    new Set(value.groupBuyRequests.map((item) => item.requestNumber)).size ===
      value.groupBuyRequests.length
  );
}

export function isCustomerGroupBuyRequestReadModel(
  value: unknown,
): value is CustomerGroupBuyRequestReadModel {
  if (!Value.Check(CustomerGroupBuyRequestReadModelSchema, value)) return false;
  const createdAt = canonicalEpoch(value.createdAt);
  const updatedAt = canonicalEpoch(value.updatedAt);
  const linked = value.linkedRedemptionNumber !== null;
  return (
    isRealBusinessDate(value.businessDate) &&
    value.requestNumber ===
      deriveCustomerGroupBuyRequestNumber(value.businessDate, value.requestId) &&
    createdAt !== null &&
    updatedAt !== null &&
    updatedAt >= createdAt &&
    (value.status === "PENDING"
      ? !linked && value.requestVersion === 1
      : linked && value.requestVersion === 2)
  );
}

export function isCustomerGroupBuyRequestMutationResult(
  value: unknown,
): value is CustomerGroupBuyRequestMutationResult {
  return (
    Value.Check(CustomerGroupBuyRequestMutationResultSchema, value) &&
    isCustomerGroupBuyRequestReadModel(value.request)
  );
}

export function isCustomerSelfServiceMutationResult(
  value: unknown,
): value is CustomerSelfServiceMutationResult {
  return (
    Value.Check(CustomerSelfServiceMutationResultSchema, value) &&
    isCustomerSelfServiceReservationReadModel(value.reservation) &&
    (value.operation === "CREATE"
      ? value.reservation.state === "PENDING_CONFIRMATION"
      : value.reservation.state === "CANCELLED")
  );
}

export function isCustomerVisitSessionRevokeRequest(
  value: unknown,
): value is CustomerVisitSessionRevokeRequest {
  return Value.Check(CustomerVisitSessionRevokeRequestSchema, value);
}

export function isCustomerVisitSessionRevokeResult(
  value: unknown,
): value is CustomerVisitSessionRevokeResult {
  return (
    Value.Check(CustomerVisitSessionRevokeResultSchema, value) &&
    isCustomerVisitSessionReadModel(value.session) &&
    value.session.status === "REVOKED"
  );
}
